import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Bot, User as UserIcon, CheckCircle2, Paperclip, Image as ImageIcon } from "lucide-react";
import {
  closeFeedback,
  replyFeedbackWithImage,
  startFeedbackWithImage,
  uploadFeedbackImage,
} from "../../api/feedback.api";
import FeedbackImagePicker from "./FeedbackImagePicker";

// Floating feedback chat widget. Mounted once per authenticated layout.
// First message starts a thread; subsequent messages reply on the same
// thread. Bot replies are inserted by the backend after each user
// message (via the Claude classifier + optional follow-up question).
//
// Persists draft + open-state across navigation in localStorage so a
// user typing while they navigate doesn't lose their thought.
//
// Props:
//   position  — "right" (default) or "left". Right is the canonical spot;
//               BallLayout uses "left" because ScorerChat owns the right
//               corner on the scoring screen.
//   compact   — when true, hide the "Feedback" label always (icon-only).
//               BallLayout passes this so the pill stays small on the
//               minimal scoring console.
const STORAGE_KEY_OPEN  = "zcd_feedback_open";
const STORAGE_KEY_DRAFT = "zcd_feedback_draft";

export default function FeedbackWidget({ position = "right", compact = false }) {
  const sideClass = position === "left" ? "left-4" : "right-4";
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_OPEN) === "1"; } catch { return false; }
  });
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_DRAFT) || ""; } catch { return ""; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [closed, setClosed] = useState(false);
  const scrollRef = useRef(null);

  // Pending annotated screenshot — uploaded to S3 the moment the user
  // accepts the annotation; we hold the s3_key + a local preview URL
  // until they hit Send. "Pick" view replaces the chat surface when
  // pickerOpen is true.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // {s3_key, previewUrl}

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_OPEN, open ? "1" : "0"); } catch {}
  }, [open]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_DRAFT, input); } catch {}
  }, [input]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    // Allow sending if EITHER text or pending image is non-empty
    if ((!text && !pendingImage) || busy) return;
    setBusy(true); setError("");

    const imageKey = pendingImage?.s3_key || "";
    const previewUrl = pendingImage?.previewUrl || null;

    // Optimistically render the user's message
    const optimistic = {
      role: "user",
      content: text || "(see attached)",
      image_url: previewUrl,
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    setPendingImage(null);
    try { localStorage.removeItem(STORAGE_KEY_DRAFT); } catch {}

    try {
      const res = thread
        ? await replyFeedbackWithImage(thread.id, text || "(see attached)", imageKey)
        : await startFeedbackWithImage(text || "(see attached)", location.pathname, imageKey);
      setThread(res.data);
      setMessages(res.data.messages || []);
    } catch (e) {
      const msg =
        e?.response?.status === 429 ? "Slow down — too many feedback messages this hour." :
        e?.response?.data?.message || e?.message || "Could not send. Try again.";
      setError(msg);
      // Roll back the optimistic insert; restore text + image
      setMessages((prev) => prev.filter((m) => !m._optimistic));
      setInput(text);
      if (imageKey) setPendingImage({ s3_key: imageKey, previewUrl });
    } finally {
      setBusy(false);
    }
  };

  const handleImagePicked = async (blob) => {
    setPickerOpen(false);
    setBusy(true); setError("");
    try {
      const res = await uploadFeedbackImage(blob);
      // Use the local blob URL for instant preview; the server-presigned URL
      // stays valid for the actual send. Browser revokes blob URL on unload.
      const previewUrl = URL.createObjectURL(blob);
      setPendingImage({ s3_key: res.data.s3_key, previewUrl });
    } catch (e) {
      setError(e?.response?.data?.message || "Image upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const removePendingImage = () => {
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleClose = async () => {
    if (!thread) {
      setOpen(false);
      return;
    }
    try { await closeFeedback(thread.id); } catch { /* ignore */ }
    setClosed(true);
    setTimeout(() => {
      setOpen(false);
      setThread(null);
      setMessages([]);
      setClosed(false);
    }, 1200);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`fixed ${sideClass} z-40 bg-button-primary text-white rounded-full ${
          compact ? "p-2.5" : "px-4 py-2.5"
        } shadow-lg hover:opacity-90 flex items-center gap-2 text-sm font-medium`}
        style={{ bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        title="Send feedback"
        aria-label="Send feedback"
      >
        <MessageCircle className="w-4 h-4" />
        {!compact && <span className="hidden sm:inline">Feedback</span>}
      </button>
    );
  }

  return (
    <div data-zcd-feedback-chrome className={`fixed ${sideClass} z-40 bg-bg-primary border border-border-default rounded-xl shadow-2xl w-[92vw] max-w-md flex flex-col overflow-hidden`} style={{ maxHeight: "80vh", bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-default bg-bg-secondary/50">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-button-primary" />
          <span className="text-sm font-semibold text-text-primary">Feedback</span>
        </div>
        <button
          onClick={handleClose}
          className="text-text-secondary hover:text-text-primary"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {closed ? (
        <div className="p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <p className="text-sm text-text-primary">Thanks — feedback closed.</p>
        </div>
      ) : pickerOpen ? (
        <FeedbackImagePicker
          onPicked={handleImagePicked}
          onCancel={() => setPickerOpen(false)}
        />
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {messages.length === 0 && (
              <div className="text-xs text-text-secondary px-2 pb-2">
                <p>Tell us what's on your mind. Examples:</p>
                <ul className="mt-1.5 space-y-0.5 list-disc pl-4">
                  <li>"The match-type dropdown is confusing"</li>
                  <li>"I'd love a per-tournament summary"</li>
                  <li>"AI commentary feels slow"</li>
                </ul>
              </div>
            )}
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <div key={m.id ?? `opt-${i}`} className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                  {!isUser && (
                    <div className="w-7 h-7 rounded-full bg-button-primary/20 flex items-center justify-center shrink-0">
                      <Bot className="w-3.5 h-3.5 text-button-primary" />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-3 py-1.5 rounded-lg text-sm ${
                    isUser
                      ? "bg-button-primary/15 text-text-primary"
                      : "bg-bg-secondary/50 text-text-primary"
                  }`}>
                    {m.image_url && (
                      <a href={m.image_url} target="_blank" rel="noreferrer" className="block mb-1.5">
                        <img
                          src={m.image_url}
                          alt="attached"
                          className="rounded-md max-h-48 object-contain border border-border-default/50"
                        />
                      </a>
                    )}
                    {m.content}
                  </div>
                  {isUser && (
                    <div className="w-7 h-7 rounded-full bg-bg-secondary/60 flex items-center justify-center shrink-0">
                      <UserIcon className="w-3.5 h-3.5 text-text-secondary" />
                    </div>
                  )}
                </div>
              );
            })}
            {busy && (
              <div className="text-xs text-text-secondary italic px-2">Sending…</div>
            )}
          </div>

          {/* Pending image preview — shown above the input until sent */}
          {pendingImage && (
            <div className="border-t border-border-default px-2 py-2 flex items-center gap-2 bg-bg-secondary/30">
              <ImageIcon className="w-3.5 h-3.5 text-text-secondary shrink-0" />
              <img
                src={pendingImage.previewUrl}
                alt="pending attachment"
                className="h-12 w-12 object-cover rounded border border-border-default"
              />
              <span className="text-xs text-text-secondary flex-1 truncate">
                Annotated screenshot ready — will send with your next message.
              </span>
              <button
                onClick={removePendingImage}
                className="text-text-secondary hover:text-rose-400"
                title="Remove attachment"
                aria-label="Remove attachment"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {error && (
            <div className="px-3 py-1.5 text-[11px] text-rose-300 bg-rose-500/10 border-t border-rose-500/30">
              {error}
            </div>
          )}

          <div className="border-t border-border-default p-2 flex gap-2 items-center">
            <button
              onClick={() => setPickerOpen(true)}
              disabled={busy}
              className="p-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary hover:bg-bg-secondary/70 disabled:opacity-50"
              title="Attach screenshot"
              aria-label="Attach screenshot"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
              placeholder={pendingImage ? "Add a note (optional)…" : "Tell us what's on your mind…"}
              maxLength={4000}
              className="flex-1 bg-bg-secondary text-text-primary text-sm px-3 py-1.5 rounded border border-border-default focus:outline-none focus:border-button-primary disabled:opacity-50"
            />
            <button
              onClick={send}
              disabled={busy || (!input.trim() && !pendingImage)}
              className="px-3 py-1.5 rounded bg-button-primary text-white disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="px-3 pb-1.5 text-[10px] text-text-secondary">
            Tip: tap 📎 to capture this page or upload a screenshot, then mark it up.
          </div>
        </>
      )}
    </div>
  );
}
